import { validateProfileCountryIso } from "./profileCountries";
import {
  validateProfileContactEmail,
  validateProfilePhone,
} from "./phoneNumberRules";
import { validateDisplayName } from "./playerNameSearch";
import {
  PLAYING_CATEGORIES,
  PREFERRED_HANDS,
  PROFILE_BIO_MAX,
  PROFILE_CITY_MAX,
  PROFILE_COUNTRY_MAX,
  PROFILE_COVER_PHOTO_REF_MAX,
  PROFILE_DATE_OF_BIRTH_MAX,
  PROFILE_FAVORITE_SHOT_MAX,
  PROFILE_GENDERS,
  PROFILE_PHOTO_REF_MAX,
  PROFILE_STATE_MAX,
  PROFILE_CLUB_MAX,
  PROFILE_PREFERRED_EVENTS_MAX,
  PROFILE_PREFERRED_EVENT_LEN,
} from "./playerProfileConstants";

/** @typedef {import('./playerProfileTypes').PlayerProfilePatch} PlayerProfilePatch */

function trimOrNull(value, maxLen) {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  if (!s) return null;
  if (s.length > maxLen) return { error: `Must be at most ${maxLen} characters` };
  return s;
}

/**
 * v1 editable fields only (UI); schema accepts full patch when provided.
 * @param {PlayerProfilePatch} patch
 * @returns {{ ok: true; data: Record<string, unknown> } | { ok: false; error: string }}
 */
export function validatePlayerProfilePatch(patch) {
  if (!patch || typeof patch !== "object") {
    return { ok: false, error: "Invalid profile data" };
  }

  /** @type {Record<string, unknown>} */
  const data = {};

  if (patch.displayName !== undefined) {
    const v = validateDisplayName(patch.displayName);
    if (!v.ok) return v;
    data.displayName = v.displayName;
    data.normalizedName = v.normalizedName;
    data.searchKeywords = v.searchKeywords;
  }

  if (patch.firstName !== undefined) {
    const first = trimOrNull(patch.firstName, 80);
    if (first && typeof first === "object") return { ok: false, error: first.error };
    data.firstName = first;
  }

  if (patch.lastName !== undefined) {
    const last = trimOrNull(patch.lastName, 80);
    if (last && typeof last === "object") return { ok: false, error: last.error };
    data.lastName = last;
  }

  if (patch.preferredHand !== undefined) {
    const hand = patch.preferredHand === null ? "" : String(patch.preferredHand);
    if (!PREFERRED_HANDS.includes(/** @type {'' | 'Left' | 'Right'} */ (hand))) {
      return { ok: false, error: "Invalid preferred hand" };
    }
    data.preferredHand = hand;
  }

  if (patch.playingCategory !== undefined) {
    const cat = patch.playingCategory === null ? "" : String(patch.playingCategory);
    if (!PLAYING_CATEGORIES.includes(/** @type {'' | 'Singles' | 'Doubles' | 'Mixed' | 'All'} */ (cat))) {
      return { ok: false, error: "Invalid playing category" };
    }
    data.playingCategory = cat || "All";
  }

  if (patch.city !== undefined) {
    const city = trimOrNull(patch.city, PROFILE_CITY_MAX);
    if (city && typeof city === "object") return { ok: false, error: city.error };
    data.city = city;
  }

  if (patch.state !== undefined) {
    const state = trimOrNull(patch.state, PROFILE_STATE_MAX);
    if (state && typeof state === "object") return { ok: false, error: state.error };
    data.state = state;
  }

  if (patch.countryIso !== undefined) {
    const countryResult = validateProfileCountryIso(patch.countryIso);
    if (!countryResult.ok) return { ok: false, error: countryResult.error };
    if (countryResult.empty) {
      data.countryIso = null;
      data.country = null;
    } else {
      data.countryIso = countryResult.countryIso;
      data.country = countryResult.country;
    }
  } else if (patch.country !== undefined) {
    const country = trimOrNull(patch.country, PROFILE_COUNTRY_MAX);
    if (country && typeof country === "object") return { ok: false, error: country.error };
    data.country = country;
  }

  if (patch.bio !== undefined) {
    const bio = trimOrNull(patch.bio, PROFILE_BIO_MAX);
    if (bio && typeof bio === "object") return { ok: false, error: bio.error };
    data.bio = bio;
  }

  if (patch.gender !== undefined) {
    const gender = patch.gender === null ? "" : String(patch.gender);
    if (!PROFILE_GENDERS.includes(/** @type {typeof PROFILE_GENDERS[number]} */ (gender))) {
      return { ok: false, error: "Invalid gender" };
    }
    data.gender = gender || null;
  }

  if (patch.dateOfBirth !== undefined) {
    const dob = trimOrNull(patch.dateOfBirth, PROFILE_DATE_OF_BIRTH_MAX);
    if (dob && typeof dob === "object") return { ok: false, error: dob.error };
    data.dateOfBirth = dob;
  }

  if (patch.club !== undefined) {
    const club = trimOrNull(patch.club, PROFILE_CLUB_MAX);
    if (club && typeof club === "object") return { ok: false, error: club.error };
    data.club = club;
  }

  if (patch.preferredEvents !== undefined) {
    const raw = Array.isArray(patch.preferredEvents)
      ? patch.preferredEvents
      : String(patch.preferredEvents || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
    if (raw.length > PROFILE_PREFERRED_EVENTS_MAX) {
      return { ok: false, error: `At most ${PROFILE_PREFERRED_EVENTS_MAX} preferred events` };
    }
    const events = [];
    for (const item of raw) {
      const s = String(item).trim().slice(0, PROFILE_PREFERRED_EVENT_LEN);
      if (s) events.push(s);
    }
    data.preferredEvents = events;
  }

  if (patch.favoriteShot !== undefined) {
    const shot = trimOrNull(patch.favoriteShot, PROFILE_FAVORITE_SHOT_MAX);
    if (shot && typeof shot === "object") return { ok: false, error: shot.error };
    data.favoriteShot = shot;
  }

  if (patch.yearsPlaying !== undefined) {
    if (patch.yearsPlaying === null || patch.yearsPlaying === "") {
      data.yearsPlaying = null;
    } else {
      const n = Number(patch.yearsPlaying);
      if (!Number.isInteger(n) || n < 0 || n > 80) {
        return { ok: false, error: "Years playing must be between 0 and 80" };
      }
      data.yearsPlaying = n;
    }
  }

  if (patch.profilePhoto !== undefined) {
    if (patch.profilePhoto === null || patch.profilePhoto === "") {
      data.profilePhoto = null;
    } else {
      const ref = String(patch.profilePhoto).trim();
      if (ref.length > PROFILE_PHOTO_REF_MAX) {
        return { ok: false, error: "Profile photo reference is too long" };
      }
      data.profilePhoto = ref;
    }
  }

  if (patch.photoUpdatedAt !== undefined) {
    if (patch.photoUpdatedAt === null) {
      data.photoUpdatedAt = null;
    } else {
      const n = Number(patch.photoUpdatedAt);
      if (!Number.isFinite(n) || n < 0) {
        return { ok: false, error: "Invalid photoUpdatedAt" };
      }
      data.photoUpdatedAt = Math.trunc(n);
    }
  }

  if (patch.coverPhoto !== undefined) {
    if (patch.coverPhoto === null || patch.coverPhoto === "") {
      data.coverPhoto = null;
    } else {
      const ref = String(patch.coverPhoto).trim();
      if (ref.length > PROFILE_COVER_PHOTO_REF_MAX) {
        return { ok: false, error: "Cover photo reference is too long" };
      }
      data.coverPhoto = ref;
    }
  }

  if (patch.isProfilePublic !== undefined) {
    data.isProfilePublic = Boolean(patch.isProfilePublic);
  }

  if (patch.contactEmail !== undefined) {
    const emailResult = validateProfileContactEmail(patch.contactEmail);
    if (!emailResult.ok) return { ok: false, error: emailResult.error };
    data.contactEmail = emailResult.empty ? null : emailResult.contactEmail;
  }

  if (
    patch.phoneCountryIso !== undefined ||
    patch.phoneNationalNumber !== undefined ||
    patch.phoneDialCode !== undefined
  ) {
    const iso =
      patch.phoneCountryIso === undefined || patch.phoneCountryIso === null
        ? ""
        : String(patch.phoneCountryIso);
    const national =
      patch.phoneNationalNumber === undefined || patch.phoneNationalNumber === null
        ? ""
        : String(patch.phoneNationalNumber);
    const phoneResult = validateProfilePhone(iso, national);
    if (!phoneResult.ok) return { ok: false, error: phoneResult.error };
    if (phoneResult.empty) {
      data.phoneCountryIso = null;
      data.phoneDialCode = null;
      data.phoneNationalNumber = null;
    } else {
      data.phoneCountryIso = phoneResult.phoneCountryIso;
      data.phoneDialCode = phoneResult.phoneDialCode;
      data.phoneNationalNumber = phoneResult.phoneNationalNumber;
    }
  }

  return { ok: true, data };
}
